import React, { useEffect, useState } from 'react';
import { getSettings, updateSettings } from '../lib/api';
import { Settings, Save, AlertCircle, Check } from 'lucide-react';

export default function SettingsPage() {
  const [settings, setSettings] = useState({});
  const [biltyDate, setBiltyDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    getSettings().then(r => {
      setSettings(r.data || {});
      if (r.data?.bilty_applicable_from) {
        const d = new Date(r.data.bilty_applicable_from);
        setBiltyDate(d.toISOString().split('T')[0]);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await updateSettings({ bilty_applicable_from: biltyDate || '' });
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Save failed' });
    }
    setSaving(false);
  };

  if (loading) return <div className="h-64 bg-white rounded-xl animate-pulse" />;

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold text-boss-text">Settings</h1>
        <p className="text-boss-text-muted text-sm">System configuration for BoSS</p>
      </div>

      {message && (
        <div className={`px-4 py-3 rounded-xl text-sm flex items-center gap-2 ${message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
          {message.type === 'success' ? <Check size={16} /> : <AlertCircle size={16} />} {message.text}
        </div>
      )}

      {/* Company Info */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-boss-border">
        <h3 className="font-semibold text-boss-text mb-4">Company Information</h3>
        <div className="grid grid-cols-2 gap-4">
          <div><label className="text-xs text-boss-text-muted">Company Name</label><div className="text-sm font-medium">{settings.company_name || '---'}</div></div>
          <div><label className="text-xs text-boss-text-muted">GSTIN</label><div className="text-sm font-mono">{settings.gstin || '---'}</div></div>
          <div className="col-span-2"><label className="text-xs text-boss-text-muted">Address</label><div className="text-sm">{settings.address || '---'}</div></div>
          <div><label className="text-xs text-boss-text-muted">Current FY</label><div className="text-sm font-medium">{settings.current_fy || '---'}</div></div>
        </div>
      </div>

      {/* Bilty Date Control */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-boss-border">
        <h3 className="font-semibold text-boss-text mb-2">Bilty Upload Control</h3>
        <p className="text-sm text-boss-text-muted mb-4">
          Set the date from which bilty upload will be available. Invoices before this date will not have the bilty upload option.
          This is useful when importing historical data that doesn't need bilty tracking.
        </p>

        <div className="flex items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-boss-text mb-1">Bilty Applicable From</label>
            <input
              type="date"
              value={biltyDate}
              onChange={e => setBiltyDate(e.target.value)}
              className="px-4 py-2 rounded-lg border border-boss-border text-sm focus:ring-2 focus:ring-boss-violet/30 focus:outline-none"
              data-testid="bilty-date-input"
            />
          </div>
          <button onClick={() => setBiltyDate('')} className="px-4 py-2 text-sm border border-boss-border rounded-lg hover:bg-boss-surface-2">
            Clear
          </button>
        </div>

        {biltyDate ? (
          <div className="mt-3 text-sm text-blue-700 bg-blue-50 p-3 rounded-lg flex items-center gap-2">
            <AlertCircle size={14} /> Bilty upload will only be available for invoices dated {biltyDate} or later
          </div>
        ) : (
          <div className="mt-3 text-sm text-green-700 bg-green-50 p-3 rounded-lg flex items-center gap-2">
            <Check size={14} /> Bilty upload is available for all invoices
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-6 py-2.5 bg-boss-violet text-white rounded-lg font-medium hover:bg-boss-violet-2 disabled:opacity-50" data-testid="settings-save-btn">
          <Save size={16} /> {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}
